import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";

interface Props {
  src: string;
  alt?: string;
  downloadName?: string;
  onClose: () => void;
}

interface View {
  scale: number;
  x: number;
  y: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP_PX = 8;

const RESET: View = { scale: 1, x: 0, y: 0 };

/**
 * Full-screen receipt viewer with wheel / pinch zoom, drag to pan and
 * double-tap to zoom. Translation is relative to the stage centre.
 */
export function ReceiptImageViewer({ src, alt = "Receipt", downloadName, onClose }: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [view, setViewState] = useState<View>(RESET);
  const viewRef = useRef<View>(RESET);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    startX: number;
    startY: number;
    moved: boolean;
    pinchDist: number;
    pinchMid: { x: number; y: number };
  } | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  const clampView = useCallback((v: View): View => {
    const stage = stageRef.current;
    const img = imgRef.current;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale));
    if (!stage || !img) return { scale, x: v.x, y: v.y };
    const maxX = Math.max(0, (img.offsetWidth * scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * scale - stage.clientHeight) / 2);
    return {
      scale,
      x: Math.min(maxX, Math.max(-maxX, v.x)),
      y: Math.min(maxY, Math.max(-maxY, v.y)),
    };
  }, []);

  const setView = useCallback(
    (next: View) => {
      const clamped = clampView(next);
      viewRef.current = clamped;
      setViewState(clamped);
    },
    [clampView],
  );

  /** Point relative to the stage centre, in CSS px. */
  const toStage = useCallback((clientX: number, clientY: number) => {
    const r = stageRef.current!.getBoundingClientRect();
    return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
  }, []);

  /** Zoom to `nextScale` keeping the stage point `p` fixed on screen. */
  const zoomAt = useCallback(
    (nextScale: number, p: { x: number; y: number }, base: View = viewRef.current) => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const k = scale / base.scale;
      setView({ scale, x: p.x - (p.x - base.x) * k, y: p.y - (p.y - base.y) * k });
    },
    [setView],
  );

  const zoomBy = useCallback(
    (factor: number) => zoomAt(viewRef.current.scale * factor, { x: 0, y: 0 }),
    [zoomAt],
  );

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      zoomAt(viewRef.current.scale * factor, toStage(e.clientX, e.clientY));
    }
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [toStage, zoomAt]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") zoomBy(1.25);
      else if (e.key === "-") zoomBy(0.8);
      else if (e.key === "0") setView(RESET);
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    const onResize = () => setView(viewRef.current);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [onClose, setView, zoomBy]);

  useEffect(() => {
    setView(RESET);
  }, [src, setView]);

  function pinchInfo() {
    const [a, b] = [...pointers.current.values()];
    return {
      dist: Math.hypot(b.x - a.x, b.y - a.y),
      mid: toStage((a.x + b.x) / 2, (a.y + b.y) / 2),
    };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 1) {
      gesture.current = {
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        pinchDist: 0,
        pinchMid: { x: 0, y: 0 },
      };
    } else if (pointers.current.size === 2 && gesture.current) {
      const { dist, mid } = pinchInfo();
      gesture.current.moved = true;
      gesture.current.pinchDist = dist;
      gesture.current.pinchMid = mid;
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const prev = pointers.current.get(e.pointerId);
    const g = gesture.current;
    if (!prev || !g) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const { dist, mid } = pinchInfo();
      const v = viewRef.current;
      const panned = { ...v, x: v.x + mid.x - g.pinchMid.x, y: v.y + mid.y - g.pinchMid.y };
      if (g.pinchDist > 0) zoomAt(v.scale * (dist / g.pinchDist), mid, panned);
      else setView(panned);
      g.pinchDist = dist;
      g.pinchMid = mid;
      return;
    }

    if (Math.hypot(e.clientX - g.startX, e.clientY - g.startY) > TAP_SLOP_PX) {
      g.moved = true;
    }
    const v = viewRef.current;
    setView({ ...v, x: v.x + e.clientX - prev.x, y: v.y + e.clientY - prev.y });
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;

    if (pointers.current.size === 1 && g) {
      // Pinch ended with one finger still down: continue as a pan from here.
      g.pinchDist = 0;
      return;
    }
    if (pointers.current.size > 0) return;
    gesture.current = null;
    if (!g || g.moved || e.type === "pointercancel") return;

    const now = performance.now();
    const tap = lastTap.current;
    if (
      tap &&
      now - tap.t < DOUBLE_TAP_MS &&
      Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < TAP_SLOP_PX * 3
    ) {
      lastTap.current = null;
      if (viewRef.current.scale > 1.01) setView(RESET);
      else zoomAt(DOUBLE_TAP_SCALE, toStage(e.clientX, e.clientY), RESET);
    } else {
      lastTap.current = { t: now, x: e.clientX, y: e.clientY };
    }
  }

  const zoomed = view.scale > 1.01;

  return createPortal(
    <div className="image-viewer" role="dialog" aria-modal="true" aria-label={alt}>
      <div className="image-viewer-toolbar">
        <button
          type="button"
          className="btn btn-sm image-viewer-btn"
          onClick={() => zoomBy(0.8)}
          disabled={view.scale <= MIN_SCALE}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className="btn btn-sm image-viewer-btn image-viewer-pct"
          onClick={() => setView(RESET)}
          aria-label="Reset zoom"
        >
          {Math.round(view.scale * 100)}%
        </button>
        <button
          type="button"
          className="btn btn-sm image-viewer-btn"
          onClick={() => zoomBy(1.25)}
          disabled={view.scale >= MAX_SCALE}
          aria-label="Zoom in"
        >
          +
        </button>
        <span style={{ flex: 1 }} />
        {downloadName && (
          <a className="btn btn-sm image-viewer-btn" href={src} download={downloadName}>
            Download
          </a>
        )}
        <button
          type="button"
          className="btn btn-sm image-viewer-btn"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div
        ref={stageRef}
        className={`image-viewer-stage${zoomed ? " zoomed" : ""}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className="image-viewer-img"
          draggable={false}
          onLoad={() => setView(RESET)}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          }}
        />
      </div>

      <div className="image-viewer-hint">
        Pinch or scroll to zoom · drag to pan · double-tap to toggle zoom
      </div>
    </div>,
    document.body,
  );
}
